import React, { useEffect, useState } from "react";
import ContainerDashboard from "../../components/layouts/ContainerDashboard";
import Loader from "../Loader";
import swal from "sweetalert";
import { axiosInstance } from "../Config";
import Pagination, { PerPageSelect } from "../../components/Pagination";
import { usePermissionData } from "../../AppContext";

const PLAN_COLORS = [
  "#A44CD7",
  "#0CACC2",
  "#F55145",
  "#F9BE22",
  "#8A3BBD",
  "#19C37D",
  "#5A79DF",
  "#EC2434",
];

const emptyForm = {
  chatTokensPerMonth: "",
  imageGenerationPerMonth: "",
  videoGenerationPerMonth: "",
};

const labelStyle = {
  fontSize: "11px",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: "#999",
};

const FIELDS = [
  {
    key: "chatTokensPerMonth",
    label: "Chat Token Limit / Month",
    icon: "bi bi-chat-left-dots",
    desc: "AI chat tokens, resets monthly",
  },
  {
    key: "imageGenerationPerMonth",
    label: "Image Generation Qty / Month",
    icon: "bi bi-image",
    desc: "Number of images, resets monthly",
  },
  {
    key: "videoGenerationPerMonth",
    label: "Video Generation Qty / Month",
    icon: "bi bi-camera-video",
    desc: "Number of videos, resets monthly",
  },
];

const toDisplayNumber = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString() : "—";
};

const getPlanName = (plan) => plan?.name ?? plan?.planName ?? "—";

const getPlanValues = (plan) => ({
  chatTokensPerMonth:
    plan?.chatTokensPerMonth ??
    plan?.tokensPerMonth ??
    plan?.chat_tokens_per_month ??
    "",
  imageGenerationPerMonth: plan?.imageGenerationPerMonth ?? "",
  videoGenerationPerMonth: plan?.videoGenerationPerMonth ?? "",
});

const AITokenLimits = () => {
  const { checkAccess } = usePermissionData();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "AI Token Limits | Mindora";
    window.scrollTo(0, 0);
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("kid-plans");
      setPlans(res.data?.data ?? res.data ?? []);
    } catch {
      swal("", "Failed to load plans.", "error");
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan =
    plans.find((p) => String(p.id) === String(selectedPlanId)) ?? null;

  const openLimitsModal = (plan) => {
    if (!plan) return;
    setSelectedPlanId(plan.id);
    setForm(getPlanValues(plan));
    setErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPlanId(null);
    setForm(emptyForm);
    setErrors({});
  };

  const validate = () => {
    const errs = {};
    if (!selectedPlan) errs.plan = "No plan available.";
    [
      "chatTokensPerMonth",
      "imageGenerationPerMonth",
      "videoGenerationPerMonth",
    ].forEach((key) => {
      const v = form[key];
      if (v === "" || v === null || v === undefined) {
        errs[key] = "Required.";
      } else if (Number.isNaN(Number(v)) || Number(v) < 0) {
        errs[key] = "Must be 0 or more.";
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    const body = {
      name: selectedPlan.name ?? selectedPlan.planName,
      description: selectedPlan.description ?? "",
      amount: Number(selectedPlan.amount),
      chatTokensPerMonth: Number(form.chatTokensPerMonth),
      imageGenerationPerMonth: Number(form.imageGenerationPerMonth),
      videoGenerationPerMonth: Number(form.videoGenerationPerMonth),
    };
    try {
      await axiosInstance.put(`kid-plans/${selectedPlan.id}`, body);
      swal("", "Token limits updated successfully!", "success");
      closeModal();
      fetchPlans();
    } catch (err) {
      const data = err?.response?.data;
      const details = Array.isArray(data?.errors)
        ? data.errors
            .map((e) => e?.message || e?.msg || JSON.stringify(e))
            .join(", ")
        : typeof data?.errors === "object" && data?.errors !== null
        ? Object.values(data.errors).flat().join(", ")
        : "";
      setErrors({
        plan: details || data?.message || "Something went wrong.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const totalPages = Math.ceil(plans.length / itemsPerPage);
  const pagedPlans = plans.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage,
  );

  return (
    <>
      {loading && <Loader />}
      <ContainerDashboard
        title="AI Token Limits"
        description="Manage chat token limit & image/video generation qty per month per kid plan"
      >
        <section className="ps-dashboards p-4">
          {/* Table heading */}
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h6
              className="fw-bold mb-0"
              style={{ color: "#1a1a2e", fontSize: 16 }}
            >
              Kids Plans
              <span
                className="ms-2 badge rounded-pill"
                style={{
                  background: "#A44CD71A",
                  color: "#A44CD7",
                  fontSize: 12,
                }}
              >
                {plans.length}
              </span>
            </h6>
            <PerPageSelect
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              setPage={setPage}
            />
          </div>

          <div
            style={{
              overflowX: "auto",
              width: "100%",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <table className="jean-table table" style={{ minWidth: "900px" }}>
              <tbody>
                {pagedPlans.map((plan, index) => {
                  const chat =
                    plan.chatTokensPerMonth ??
                    plan.tokensPerMonth ??
                    plan.chat_tokens_per_month;
                  return (
                    <tr key={plan.id}>
                      <td width={46}>
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center"
                          style={{
                            width: 40,
                            height: 40,
                            background: PLAN_COLORS[index % PLAN_COLORS.length],
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: 14,
                            flexShrink: 0,
                          }}
                        >
                          <i
                            className="bi bi-star-fill"
                            style={{ fontSize: 14 }}
                          ></i>
                        </div>
                      </td>
                      <td>
                        <div className="therapist-info">
                          <p
                            className="mb-1"
                            style={{
                              fontSize: "11px",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              color: "#999",
                            }}
                          >
                            Plan Name
                          </p>
                          <p className="mb-0 fw-bold">{getPlanName(plan)}</p>
                          {plan.description ? (
                            <p
                              className="mb-0"
                              style={{ fontSize: 12, color: "#777" }}
                            >
                              {plan.description}
                            </p>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <p style={labelStyle} className="mb-1">
                          <i
                            className="bi bi-chat-left-dots me-1"
                            style={{ color: "#0CACC2" }}
                          ></i>
                          Chat Token / Month
                        </p>
                        <p
                          className="mb-0 fw-semibold"
                          style={{ fontSize: 14, color: "#1a1a2e" }}
                        >
                          {toDisplayNumber(chat)}
                        </p>
                      </td>
                      <td>
                        <p style={labelStyle} className="mb-1">
                          <i
                            className="bi bi-image me-1"
                            style={{ color: "#A44CD7" }}
                          ></i>
                          Image / Month
                        </p>
                        <p
                          className="mb-0 fw-semibold"
                          style={{ fontSize: 14, color: "#1a1a2e" }}
                        >
                          {toDisplayNumber(plan.imageGenerationPerMonth)}
                        </p>
                      </td>
                      <td>
                        <p style={labelStyle} className="mb-1">
                          <i
                            className="bi bi-camera-video me-1"
                            style={{ color: "#F55145" }}
                          ></i>
                          Video / Month
                        </p>
                        <p
                          className="mb-0 fw-semibold"
                          style={{ fontSize: 14, color: "#1a1a2e" }}
                        >
                          {toDisplayNumber(plan.videoGenerationPerMonth)}
                        </p>
                      </td>
                      <td className="pe-3">
                        <div className="d-flex justify-content-end">
                          {checkAccess(10, 3) && (
                            <button
                              type="button"
                              className="btn fw-semibold"
                              style={{
                                background: "#f5eeff",
                                color: "#A44CD7",
                                borderRadius: 8,
                                padding: "5px 12px",
                                fontSize: 12,
                                border: "1px solid #e0c8ff",
                                whiteSpace: "nowrap",
                              }}
                              onClick={() => openLimitsModal(plan)}
                            >
                              <i className="bi bi-sliders me-1"></i>Set Limits
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {plans.length === 0 && !loading && (
                  <tr>
                    <td colSpan={6}>
                      <div className="alert alert-success ps-4 m-2">
                        No plans found!
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            setPage={setPage}
            totalItems={plans.length}
            showingCount={pagedPlans.length}
          />
        </section>
      </ContainerDashboard>

      {showModal && (
        <TokenLimitsModal
          selectedPlan={selectedPlan}
          form={form}
          setForm={setForm}
          errors={errors}
          onSubmit={handleSubmit}
          onClose={closeModal}
          submitting={submitting}
        />
      )}
    </>
  );
};

const TokenLimitsModal = ({
  selectedPlan,
  form,
  setForm,
  errors,
  onSubmit,
  onClose,
  submitting,
}) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.45)",
      zIndex: 99999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
    }}
    onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}
  >
    <div
      style={{
        width: "100%",
        maxWidth: 520,
        background: "#fff",
        borderRadius: 20,
        overflow: "hidden",
        boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
        maxHeight: "92vh",
        overflowY: "auto",
      }}
    >
      <div
        className="d-flex align-items-center gap-3 px-4 py-3"
        style={{ background: "linear-gradient(135deg, #A44CD7, #0CACC2)" }}
      >
        <i
          className="bi bi-sliders"
          style={{ fontSize: 22, color: "#fff" }}
        ></i>
        <div className="flex-grow-1">
          <h6 className="mb-0 fw-bold text-white">
            Set Limits{selectedPlan ? ` — "${getPlanName(selectedPlan)}"` : ""}
          </h6>
          <p
            className="mb-0"
            style={{ fontSize: 12, color: "rgba(255,255,255,0.8)" }}
          >
            Enter chat token limit & image/video generation qty per month for
            this kid plan.
          </p>
        </div>
        <button
          onClick={onClose}
          style={{
            background: "rgba(255,255,255,0.2)",
            border: "none",
            borderRadius: "50%",
            width: 32,
            height: 32,
            color: "#fff",
            fontSize: 16,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          ✕
        </button>
      </div>

      <div className="p-4">
        {errors.plan && (
          <p className="mb-3 text-danger" style={{ fontSize: 12 }}>
            {errors.plan}
          </p>
        )}

        <div className="row g-3 mb-4">
          {FIELDS.map((field, i) => (
            <div className={i === 0 ? "col-12" : "col-6"} key={field.key}>
              <label
                className="form-label fw-semibold"
                style={{ fontSize: 13 }}
              >
                <i
                  className={field.icon + " me-1"}
                  style={{ color: "#A44CD7" }}
                ></i>
                {field.label} <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                className="form-control"
                style={{ borderRadius: 10, fontSize: 14 }}
                value={form[field.key]}
                onChange={(e) =>
                  setForm((p) => ({ ...p, [field.key]: e.target.value }))
                }
                placeholder="0"
                min="0"
              />
              {errors[field.key] && (
                <p className="mb-0 mt-1 text-danger" style={{ fontSize: 12 }}>
                  {errors[field.key]}
                </p>
              )}
              <p className="mb-0 mt-1" style={{ fontSize: 11, color: "#aaa" }}>
                {field.desc}
              </p>
            </div>
          ))}
        </div>

        <div className="d-flex gap-2 justify-content-end">
          <button
            className="btn px-4"
            style={{
              borderRadius: 10,
              background: "#f5f5f5",
              color: "#555",
              fontSize: 13,
            }}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="btn px-4 fw-semibold text-white"
            disabled={submitting}
            style={{
              borderRadius: 10,
              background: "linear-gradient(135deg, #A44CD7, #0CACC2)",
              fontSize: 13,
              border: "none",
            }}
            onClick={onSubmit}
          >
            <i className="bi bi-check2 me-1"></i>
            {submitting ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  </div>
);

export default AITokenLimits;
